import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationItem } from '@todo/shared';
import { notificationApi } from '../api/notification.api';

/**
 * "Your task just got BLOCKED" pop-ups, bottom-right - the sibling of
 * ReminderToasts, for the delegator's side of the table.
 *
 * Event-driven like the bell (mount / focus / visibility - no polling): on
 * each check it looks at recent unread notifications and toasts any
 * TASK_BLOCKED it has not shown before. Shown ids are remembered in
 * localStorage so a refresh does not replay them.
 */

const FRESH_MS = 30 * 60_000; // only block events from the last half hour pop
const SEEN_KEY = 'cgtodo.blockedToastsSeen';

function loadSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function saveSeen(seen: Set<string>): void {
  try {
    // Keep the memory bounded - the freshness window makes old ids useless.
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-100)));
  } catch { /* private mode - toasts may repeat, nothing breaks */ }
}

export function BlockedToasts() {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<NotificationItem[]>([]);
  const seen = useRef<Set<string>>(loadSeen());

  const check = useCallback(() => {
    notificationApi
      .list({ unread: true, pageSize: 20 })
      .then((res) => {
        const fresh = res.data.filter(
          (n) =>
            n.kind === 'TASK_BLOCKED' &&
            !seen.current.has(n.id) &&
            Date.now() - new Date(n.createdAt).getTime() < FRESH_MS,
        );
        if (fresh.length === 0) return;
        for (const n of fresh) seen.current.add(n.id);
        saveSeen(seen.current);
        setToasts((current) => [...current, ...fresh]);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    check();
    function onVisible() {
      if (document.visibilityState === 'visible') check();
    }
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  function dismiss(id: string) {
    setToasts((current) => current.filter((t) => t.id !== id));
  }

  if (toasts.length === 0) return null;

  return (
    // Positioning lives in AppLayout's shared toast stack - this group only
    // stacks its own cards.
    <div className="pointer-events-none flex flex-col gap-2">
      {toasts.map((n) => (
        <div key={n.id} role="alert"
             className="pointer-events-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl ring-1 ring-black/5">
          <div className="flex items-start gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-red-100 text-red-700">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
                <path d="M10 6.5v4m0 3h.01M10 17a7 7 0 100-14 7 7 0 000 14z"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Task blocked</p>
              <p className="mt-0.5 text-sm font-medium text-slate-900">{n.title}</p>
              {n.body && <p className="mt-0.5 text-xs text-slate-500">Reason: {n.body}</p>}
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => dismiss(n.id)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
              Dismiss
            </button>
            <button type="button"
              onClick={() => { dismiss(n.id); navigate('/tasks?status=BLOCKED'); }}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-700">
              View blocked tasks
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
